import { useOneTimeStorageClear } from 'api/aws/localStorage/useOneTimeStorageClear'
import { useUnreadNotificationsCount } from 'api/aws/notifications/useNotificationsByUserId'
import { useUpdateUser } from 'api/aws/users/useUpdateUserItems'
import { useUser } from 'api/aws/useUser'
import { useActiveUser } from 'api/slack/useActiveUser'
import AccountingPage from 'components/Accounting/AccountingPage'
import ContactsPage from 'components/Contacts/ContactsPage'
import DashboardPage from 'components/Dashboard/DashboardPage'
import BulkPatchImportPage from 'components/Deals/BulkPatch/BulkPatchImportPage'
import HeartbeatPushesAdmin from 'components/Deals/HeartbeatPushesAdmin'
import DealsPage from 'components/Deals/grid/DealsPage'
import DealsPageServerSide from 'components/Deals/grid/DealsPageServerSide'
import KanbanContainer from 'components/DealsKanban/ColumnContainer'
import Drawer_NCO from 'components/NCO/Drawer_NCO'
import OptimizationsPage from 'components/Optimizations/OptimizationsPage'
import OrgContacts from 'components/Organizations/OrgContacts'
import OrgProducts from 'components/Organizations/OrgProducts'
import ProjectsPage from 'components/Projects/ProjectsPage'
import ProductInfoCard from 'components/ui/product-info-card'
import { EditorProvider } from 'context/EditorContext/EditorContext'
import { useAllItemsLive } from 'context/Reducer/useAllItemsLive'
// import { MountNonce, RenderCounter } from 'debug/mountProbes'
import SimpleOrgGrid from 'components/DataGrid/prototype/SimpleOrgGrid'
import TestGridPage from 'components/DataGrid/prototype/TestGridPage'
import DealRouting from 'components/Deals/routes/DealRouting'
import MTBPageServerSide from 'components/MTB/MTBPageServerSide'
import NCOPageServerSide from 'components/NCO/NCOPageServerSide'
import OrganizationsPageServerSide from 'components/Organizations/grid/OrganizationsPageServerSide'
import ProductsPageServerSide from 'components/ProductRows/ProductsPageServerSide'
import TeamProfile from 'components/Team/TeamProfile'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { GROBxLoader, PageContainer } from 'theme/components'
import AppLayout from './AppLayout'
import AccountingDrawer from './components/Accounting/AccountingDrawer'
import AgentChatPopoutPage from './components/AgentChat/AgentChatPopoutPage'
import MasterAgreementsPage from './components/Agreements/MasterAgreementsPage'
import ApprovalsPage from './components/Approvals/ApprovalsPage'
import Boards from './components/Boards/Boards'
import ContactDealInfo from './components/Contacts/ContactDealInfo'
import ContactOrgsInfo from './components/Contacts/ContactOrgsInfo'
import ContactPage from './components/Contacts/ContactPage'
import ContactProfile from './components/Contacts/ContactProfile'
import GooglePickerAPI from './components/Google/GooglePickerAPI'
import LeadsPage from './components/Leads/LeadsPage'
import MentionsPage from './components/Mentions/MentionsPage'
import MetricsDataGrid from './components/MetricAnalytics/MetricDataGrid'
import OrganizationPage from './components/Organizations/OrganizationPage'
import OrgHeartbeatData from './components/Organizations/OrgHeartbeatData'
import OrganizationProfile from './components/Organizations/OrganizationProfile'
import OrgDealInfo from './components/Organizations/OrgDealInfo'
import OrgTaskInfo from './components/Organizations/OrgTaskInfo'
import {
  OrganizationComments,
  OrganizationIntegration,
  OrganizationRelatedOrgs,
} from './components/Organizations/routes'
import DocumentDetails from './components/PandaDoc/DocumentDetails'
import Documents from './components/PandaDoc/Documents'
import ReportsDashboardPage from './components/Reports/ReportsDashboardPage'
import { UnderConstruction } from './components/shared'
import Strategy from './components/Strategy/Strategy'
import RenderCampaign from './components/StrategyCampaigns/RenderCampaign'
import TaskDrawer from './components/TaskDrawer/TaskDrawer'
import TeamsPage from './components/Team/TeamsPage'
import TeamTasksPage from './components/TeamTasks/TeamTasksPage'
import { ActivityReport, TimeTrackerWidget } from './components/TimeTracker'
import { DrawerProvider } from './context/DrawerProvider/DrawerProvider'
import { HelpProvider } from './context/HelpProvider/HelpProvider'
import { TimeTrackerProvider } from './context/TimeTracker'
import { ValidationProvider } from './context/ValidationProvider/ValidationProvider'
// const _Probe = ({ name, children }) => (
//   <>
//     <MountNonce name={name} />
//     <RenderCounter name={name} />
//     {children}
//   </>
// )
const ProtectedApp = () => {
  const location = useLocation()
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const { user: serverUser, isLoading } = useUser(activeUser?.user?.id)
  const storageReady = useOneTimeStorageClear({ user: serverUser, updateUser: useUpdateUser().mutate })

  // (Optional) you can keep this here or move it into the nav for isolation
  const { data: unreadCount = 0 } = useUnreadNotificationsCount(serverUser?.user_uuid, true)
  // Mount live listeners once
  useAllItemsLive()

  const ready = !isActiveUserLoading && !isLoading && storageReady

  if (!ready) return <GROBxLoader message={'Loading GROBx'} overlay />

  // Agent chat pop-out window — bypass AppLayout (no nav bar)
  if (location.pathname === '/agent-chat') return <AgentChatPopoutPage />

  // Pop-out guard: if this window was opened via window.open() and React Router
  // tries to navigate anywhere else (back button, stray link, etc.), redirect back.
  if (window.opener) return <Navigate to="/agent-chat" replace />
  return (
    <>
      {/* <BreakpointMarker breakpoint="xs" label="Extra Small (xs)" color="rgb(255, 82, 0)" />
      <BreakpointMarker breakpoint="sm" label="Small (sm)" color="rgb(54, 209, 93)" />
      <BreakpointMarker breakpoint="md" label="Medium (md)" color="rgb(0, 180, 255)" />
      <BreakpointMarker breakpoint="lg" label="Large (lg)" color="rgb(126, 0, 255)" />
      <BreakpointMarker breakpoint="xl" label="Extra Large (xl)" color="rgb(255, 0, 166)" /> */}

      <ValidationProvider>
        <DrawerProvider>
          <HelpProvider>
            <TimeTrackerProvider>
              <EditorProvider>
                <AppLayout isLoading={isLoading} unreadCount={unreadCount}>
                  <Routes>
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/boards" element={<Boards />} />
                    <Route path="/metrics" element={<MetricsDataGrid />} />
                    <Route path="/reports" element={<ReportsDashboardPage />} />
                    <Route path="/products" element={<ProductsPageServerSide />} />
                    <Route path="/products/:PK/:SK/details" element={<ProductInfoCard />} />
                    <Route path="/teams" element={<TeamsPage />} />
                    <Route
                      path="/teams/profile/:userId"
                      element={
                        <PageContainer autoOverflow height="calc(100vh + 60px)" padding={3}>
                          <TeamProfile />
                        </PageContainer>
                      }
                    />
                    <Route
                      path="/activity-report"
                      element={
                        <PageContainer autoOverflow height="calc(100vh + 60px)">
                          <ActivityReport />
                        </PageContainer>
                      }
                    />
                    {/* <Route path="/test/taskgantt" element={<TestScheduleViews />} /> */}
                    {/* NCO Routes */}
                    <Route path="/optimizations" element={<OptimizationsPage />} />
                    <Route path="/nco" element={<NCOPageServerSide />}>
                      <Route path=":PK/:SK/:tab" element={<Drawer_NCO />}>
                        <Route path="main" element={null} />
                        <Route path="tasks" element={null} />
                        {/* <Route path="strategy" element={null} /> */}
                        <Route path="strategy/:campaignId" element={null} />
                        <Route path="review" element={null} />
                        <Route path="comments" element={null} />
                        <Route path="kickoff" element={null} />
                        <Route path="integration" element={null} />
                        <Route path="seo" element={null} />
                      </Route>
                    </Route>
                    <Route path="/mtb" element={<MTBPageServerSide />}>
                      {/* Child routes define valid URL patterns for useParams() - drawer renders via GlobalDataGrid */}
                      <Route path=":PK/*" element={null}>
                        <Route path="main" element={null} />
                        <Route path="seo" element={null} />
                        <Route path="am" element={null} />
                        <Route path="dms" element={null} />
                        <Route path="comments" element={null} />
                        <Route path="deals" element={null} />
                      </Route>
                    </Route>
                    <Route path="/projects" element={<ProjectsPage />}>
                      <Route path=":PK/:SK/*" element={<TaskDrawer />}>
                        <Route path="main" element={null} />
                        <Route path="review" element={null} />
                        <Route path="messages" element={null} />
                        <Route path="filemgmt" element={null} />
                        <Route path="orgtasks/:orgId" element={null} />
                      </Route>
                    </Route>
                    <Route path="/teamtasks" element={<TeamTasksPage />}>
                      <Route path=":PK/:SK/*" element={<TaskDrawer />}>
                        <Route path="main" element={null} />
                        <Route path="subtasks" element={null} />
                        <Route path="messages" element={null} />
                        <Route path="description" element={null} />
                        <Route path="filemgmt" element={null} />
                        <Route path="timeline" element={null} />
                      </Route>
                    </Route>
                    <Route path="/accounting" element={<AccountingPage />}>
                      <Route path=":PK/:SK/*" element={<AccountingDrawer />}>
                        <Route path="details" element={null} />
                        <Route path="comments" element={null} />
                      </Route>
                    </Route>
                    <Route path="/leads" element={<LeadsPage />} />
                    <Route path="/approvals" element={<ApprovalsPage />} />
                    <Route path="/notifytags" element={<MentionsPage />} />
                    <Route path="/deals" element={<DealsPageServerSide />} />
                    <Route path="/deals-legacy" element={<DealsPage />} />
                    <Route path="/deals/pipeline" element={<KanbanContainer />} />
                    <Route path="/deals/bulk-patch" element={<BulkPatchImportPage />} />
                    <Route path="/deals/heartbeat-pushes" element={<HeartbeatPushesAdmin />} />
                    {/*
                      DealRouting owns the /deals/:PK/:SK/* subtree.
                      Nested tab routes are still declared so URL
                      matching + tab navigation continue to work, but
                      the KeepAlive'd tabs use element={null} — the
                      component is rendered inside DealRouting (see
                      KeepAliveTabPanels) so it persists across tab
                      switches instead of remounting on every URL
                      change.

                      Non-KeepAlive routes (strategy/:campaignId,
                      tasks, services placeholders) keep their
                      element={...} — they render through DealRouting's
                      Outlet as normal.
                    */}
                    <Route path="/deals/:PK/:SK/*" element={<DealRouting />}>
                      <Route path="details" element={null} />
                      <Route path="agreement" element={null} />
                      <Route
                        path="tasks"
                        element={
                          <UnderConstruction
                            featureName="Deal Tasks"
                            description="Task management for this deal will be available here."
                          />
                        }
                      />
                      <Route
                        path="services"
                        element={
                          <UnderConstruction
                            featureName="Deal Services"
                            description="Service details for this deal will be available here."
                          />
                        }
                      />
                      <Route path="comments" element={null} />
                      <Route path="strategy" element={<Strategy />} />
                      <Route path="strategy/:campaignId" element={<RenderCampaign />} />
                      <Route path="review" element={null} />
                      <Route path="schedule" element={null} />
                      <Route path="invoicing" element={null} />
                      <Route path="timeline" element={null} />
                      <Route path="versions" element={null} />
                      <Route path="heartbeat" element={null} />
                    </Route>
                    <Route path="/contacts" element={<ContactsPage />} />
                    <Route path="/contacts/:contactId/*" element={<ContactPage />}>
                      <Route path="details" element={<ContactProfile />} />
                      <Route path="deals" element={<ContactDealInfo />} />
                      <Route path="organizations" element={<ContactOrgsInfo />} />
                      <Route path="additionalinfo" element={<div>additionalInfo</div>} />
                    </Route>
                    <Route path="/organizations" element={<OrganizationsPageServerSide />} />
                    <Route path="/organizations/:PK/*" element={<OrganizationPage />}>
                      <Route index element={<Navigate to="details" replace />} />
                      <Route path="details" element={<OrganizationProfile />} />
                      <Route path="deals" element={<OrgDealInfo />} />
                      <Route path="agreements" element={<MasterAgreementsPage />} />
                      <Route path="products" element={<OrgProducts />} />
                      <Route path="contacts" element={<OrgContacts />} />
                      <Route path="tasks" element={<OrgTaskInfo />} />
                      <Route path="comments" element={<OrganizationComments />} />
                      <Route path="integration" element={<OrganizationIntegration />} />
                      <Route path="heartbeat" element={<OrgHeartbeatData />} />
                      <Route path="relatedorgs" element={<OrganizationRelatedOrgs />} />
                    </Route>
                    <Route path="/pandadocs" element={<Documents />} />
                    <Route path="/pandadocs/:name" element={<DocumentDetails />} />
                    <Route path="/googledrives" element={<GooglePickerAPI />} />
                    {/* Prototype Grid for testing UnifiedDataGrid */}
                    <Route path="/test-grid" element={<TestGridPage />} />
                    {/* Simplified org grid for debugging data flow */}
                    <Route path="/simple-grid" element={<SimpleOrgGrid />} />
                  </Routes>
                </AppLayout>
                {/* Time Tracker Widget - visibility controlled via nav button */}
                <TimeTrackerWidget position="bottom-right" />
              </EditorProvider>
            </TimeTrackerProvider>
          </HelpProvider>
        </DrawerProvider>
      </ValidationProvider>
    </>
  )
}

export default ProtectedApp
